import type { Messages } from './index';

export const en: Messages = {
  app: {
    name: 'Holymoly',
    tagline: 'Marketing platform of Bechtle PLM'
  },
  common: {
    loading: 'Loading …',
    retry: 'Try again',
    close: 'Close',
    reset: 'Reset',
    unknownError: 'An unexpected error occurred.'
  },
  nav: {
    sectionGeneral: 'General',
    overview: 'Overview',
    account: 'My account',
    linkedin: 'LinkedIn',
    campaigns: 'Campaigns',
    leads: 'Leads',
    crm: 'CRM',
    crmQueue: 'Transfers',
    crmMapping: 'Field mapping',
    dashboards: 'Dashboards',
    datasets: 'Data sources'
  },
  layout: {
    toggleSidebar: 'Toggle sidebar',
    toggleSidebarHover: 'Compact sidebar',
    toggleTheme: 'Toggle theme',
    openCustomizer: 'Customize appearance',
    language: 'Language',
    footer: 'Bechtle PLM · Marketing'
  },
  customizer: {
    title: 'Appearance',
    fullscreen: 'Full screen',
    theme: 'Theme',
    themes: {
      light: 'Light',
      contrast: 'Contrast',
      material: 'Material',
      dark: 'Dark',
      dim: 'Dim',
      'material-dark': 'Material dark',
      system: 'System'
    },
    sidebar: 'Sidebar',
    sidebarOnlyLight: '(light and contrast themes only)',
    sidebarLight: 'Light',
    sidebarDark: 'Dark'
  },
  auth: {
    signIn: 'Sign in',
    signInTitle: 'Sign in to Holymoly',
    signInLead: 'With your e-mail address and password.',
    email: 'E-mail address',
    password: 'Password',
    showPassword: 'Show password',
    forgotPassword: 'Forgot password?',
    passkey: 'Sign in with a passkey',
    or: 'or',
    invalidCredentials: 'E-mail address or password is wrong.',
    tooManyAttempts: 'Too many attempts. Please wait a moment.',
    passkeyFailed: 'Signing in with a passkey did not work.',
    twoFactorTitle: 'Verification code',
    twoFactorLead: 'Enter the 6-digit code from your authenticator app.',
    code: 'Code',
    verify: 'Verify',
    useBackupCode: 'Use a recovery code instead',
    useTotp: 'Use a code from the app instead',
    backupCode: 'Recovery code',
    invalidCode: 'The code is not correct. Please try again.',
    trustDevice: 'Trust this device for 30 days',
    signOut: 'Sign out',
    backToSignIn: 'Back to sign-in',
    forgotTitle: 'Reset password',
    forgotLead: 'We will send you a link to set a new password.',
    sendLink: 'Send link',
    forgotSent: 'If an account exists for this address, the link is on its way. It is valid for one hour.',
    resetTitle: 'Set a new password',
    newPassword: 'New password',
    confirmPassword: 'Repeat password',
    passwordHint: 'At least 12 characters. A sentence of several words is easy to remember and secure.',
    passwordsDiffer: 'The passwords do not match.',
    passwordTooShort: 'The password must be at least 12 characters long.',
    resetDone: 'Your password was changed. You can sign in now.',
    resetInvalid: 'This link is invalid or expired. Please request a new one.',
    savePassword: 'Save password',
    inviteTitle: 'Welcome to Holymoly',
    inviteLead: 'You were invited as {{email}}. Set your name and password.',
    inviteInvalid: 'This invitation is invalid, expired or already used. Please ask for a new one.',
    yourName: 'Your name',
    createAccount: 'Create account',
    sessionExpired: 'Your session expired. Please sign in again.'
  },
  account: {
    title: 'My account',
    tabProfile: 'Profile',
    tabSecurity: 'Security',
    tabTokens: 'API tokens',
    name: 'Name',
    language: 'Interface language',
    save: 'Save',
    saved: 'Saved.',
    changePassword: 'Change password',
    currentPassword: 'Current password',
    passwordChanged: 'Password changed. Other sessions were signed out.',
    twoFactor: 'Two-factor authentication',
    twoFactorRequired: 'Your role requires two-factor authentication. Set it up now to use Holymoly.',
    twoFactorOn: 'Active',
    twoFactorOff: 'Not set up',
    enableTwoFactor: 'Set up',
    disableTwoFactor: 'Disable',
    twoFactorScan: 'Scan the QR code with your authenticator app (e.g. Microsoft Authenticator).',
    twoFactorQrAlt: 'QR code for the authenticator app',
    twoFactorStep1: 'Can’t scan? Choose “Enter key manually” in the app and use this key:',
    twoFactorOpenApp: 'Open directly in the app on your phone',
    twoFactorStep2: 'Then enter the 6-digit code from the app:',
    secretKey: 'Key',
    copy: 'Copy',
    copied: 'Copied',
    backupCodes: 'Recovery codes',
    backupCodesLead: 'Keep these codes safe. Each works once if you cannot access your app.',
    confirmWithPassword: 'Confirm with your password:',
    twoFactorEnabled: 'Two-factor authentication is set up.',
    twoFactorDisabled: 'Two-factor authentication was disabled.',
    cannotDisableRequired: 'Your role requires two-factor authentication; it cannot be disabled.',
    passkeys: 'Passkeys',
    passkeysLead: 'Sign in without a password — with Windows Hello, fingerprint or a security key.',
    addPasskey: 'Add passkey',
    passkeyName: 'Label (e.g. “Laptop”)',
    noPasskeys: 'No passkeys yet.',
    remove: 'Remove',
    tokensLead: 'Personal tokens for scripts and AI assistants (REST API and MCP). A token can do at most what you can.',
    tokenName: 'Label',
    tokenScope: 'Permissions',
    tokenExpires: 'Valid for',
    days: '{{count}} days',
    createToken: 'Create token',
    tokenCreated: 'Your new token — it is shown only now:',
    noTokens: 'No tokens yet.',
    revoke: 'Revoke',
    revoked: 'Revoked',
    expired: 'Expired',
    lastUsed: 'Last used',
    never: 'never',
    expiresAt: 'Expires',
    noPermissionForTokens: 'Your role does not allow API tokens.'
  },
  admin: {
    section: 'Administration',
    users: 'Users',
    roles: 'Roles',
    audit: 'Audit log',
    invite: 'Invite',
    inviteTitle: 'Invite a user',
    inviteSent: 'Invitation sent to {{email}}.',
    inviteLinkManual: 'Mail is off. Send this link yourself (valid 7 days):',
    inviteLanguage: 'Invitation language',
    openInvites: 'Open invitations',
    resend: 'Resend',
    resent: 'New link sent; the old one no longer works.',
    revokeInvite: 'Withdraw',
    expiresOn: 'valid until {{date}}',
    inviteExpired: 'expired',
    name: 'Name',
    email: 'E-mail',
    rolesCol: 'Roles',
    status: 'Status',
    lastSeen: 'Last active',
    active: 'Active',
    deactivated: 'Deactivated',
    twoFactorShort: '2FA',
    editRoles: 'Edit roles',
    deactivate: 'Deactivate',
    reactivate: 'Reactivate',
    resetTwoFactor: 'Reset 2FA',
    confirmDeactivate: 'Deactivate {{name}}? All sessions and API tokens end immediately.',
    confirmResetTwoFactor: 'Reset 2FA for {{name}}? They must set it up again at next sign-in.',
    noRoles: 'none',
    cancel: 'Cancel',
    confirm: 'Confirm',
    newRole: 'New role',
    editRole: 'Edit role',
    roleName: 'Name',
    roleDescription: 'Description',
    members: '{{count}} members',
    system: 'System',
    requireTwoFactor: 'Require 2FA',
    requireTwoFactorForced: 'Required because the role contains sensitive permissions.',
    sensitive: 'sensitive',
    allPermissions: 'All permissions',
    deleteRole: 'Delete',
    confirmDeleteRole: 'Delete role “{{name}}”?',
    auditAction: 'Action',
    auditActor: 'By',
    auditTarget: 'Target',
    auditTime: 'Time',
    auditDetails: 'Details',
    loadMore: 'Load more',
    filterAction: 'Filter by action',
    all: 'All',
    system_actor: 'System'
  },
  permissionModules: {
    admin: 'Administration',
    account: 'Account',
    linkedin: 'LinkedIn',
    leads: 'Leads',
    crm: 'CRM',
    dashboards: 'Dashboards'
  },
  permissions: {
    'users.view': 'See users and invitations',
    'users.manage': 'Invite users, assign roles, deactivate',
    'roles.manage': 'Create and edit roles',
    'audit.view': 'View the audit log',
    'settings.manage': 'Change system settings',
    'api_tokens.create': 'Create own API tokens',
    'linkedin.view': 'See LinkedIn accounts, campaigns and statistics',
    'linkedin.connect': 'Connect and disconnect LinkedIn',
    'linkedin.webhooks.manage': 'Manage LinkedIn webhooks',
    'leads.view': 'See leads (without contact data)',
    'leads.view_pii': 'See lead contact data (e-mail, phone)',
    'leads.export': 'Export leads',
    'leads.erase': 'Erase personal data (GDPR)',
    'crm.view': 'See CRM synchronisation',
    'crm.mapping.manage': 'Edit CRM field mapping',
    'crm.sync.release': 'Release held leads to the CRM',
    'crm.sync.retry': 'Retry failed CRM transfers',
    'dashboards.view': 'View dashboards',
    'dashboards.edit': 'Edit dashboards',
    'dashboards.publish': 'Publish dashboards',
    'dashboards.sql': 'Custom SQL queries in dashboards',
    'datasets.manage': 'Manage datasets and metrics'
  },
  auditActions: {
    'invite.created': 'Invitation created',
    'invite.resent': 'Invitation resent',
    'invite.revoked': 'Invitation withdrawn',
    'invite.accepted': 'Invitation accepted',
    'user.roles_changed': 'Roles changed',
    'user.deactivated': 'User deactivated',
    'user.reactivated': 'User reactivated',
    'user.two_factor_reset': '2FA reset',
    'role.created': 'Role created',
    'role.updated': 'Role changed',
    'role.deleted': 'Role deleted',
    'api_token.created': 'API token created',
    'api_token.revoked': 'API token revoked',
    'account.profile_updated': 'Profile changed',
    'linkedin.connected': 'LinkedIn connected',
    'linkedin.reconnected': 'LinkedIn reconnected',
    'linkedin.disconnected': 'LinkedIn disconnected',
    'linkedin.sync_requested': 'Sync requested',
    'linkedin.webhooks_subscribed': 'Webhooks subscribed',
    'linkedin.webhook_deleted': 'Webhook deleted',
    'lead.viewed': 'Lead viewed with contact data',
    'leads.exported': 'Leads exported',
    'lead.erased': 'Personal data erased',
    'crm.settings_changed': 'CRM settings changed',
    'crm.campaign_map_changed': 'CRM campaign mapping changed',
    'crm.released': 'Leads released to the CRM',
    'crm.retried': 'CRM transfer retried',
    'dashboard.created': 'Dashboard created',
    'dashboard.saved': 'Dashboard draft saved',
    'dashboard.proposed': 'Dashboard proposal submitted',
    'dashboard.published': 'Dashboard published',
    'dashboard.rejected': 'Dashboard proposal rejected',
    'dashboard.access_changed': 'Dashboard sharing changed',
    'dataset.saved': 'Data source saved',
    'dataset.deleted': 'Data source deleted'
  },
  conflicts: {
    last_admin: 'The last active administrator cannot lose this role.',
    self: 'You cannot do this to yourself.',
    email_taken: 'A user with this e-mail address already exists.',
    invite_open: 'There is already an open invitation for this address.',
    system_role: 'The Administrator role cannot be changed.',
    role_in_use: 'The role is still assigned to users.',
    name_taken: 'A role with this name already exists.',
    wildcard_reserved: 'Only the Administrator role holds all permissions.',
    connection_inactive: 'The LinkedIn connection is not active. Please reconnect.',
    webhook_unreachable: 'LinkedIn cannot reach this address (public HTTPS only).',
    crm_not_configured: 'No CRM is set up on this server.',
    unknown_attribute: 'The CRM has no such field (or it cannot be filled). Reload the CRM fields if it was just created.',
    not_failed: 'Only failed transfers can be retried.',
    duplicate_campaign: 'A LinkedIn campaign is mapped twice.',
    not_proposed: 'Only proposals can be rejected.',
    bigquery_not_configured: 'BigQuery is not set up on this server.',
    query_too_expensive: 'This query would read too much data. Narrow the period or the selection.',
    dataset_not_allowed: 'This BigQuery dataset is not allowlisted.',
    dataset_in_use: 'The data source is still used in dashboards.',
    builtin_dataset: 'Built-in data sources cannot be changed.'
  },
  linkedin: {
    title: 'LinkedIn',
    section: 'LinkedIn',
    overview: 'Connection',
    campaigns: 'Campaigns',
    leads: 'Leads',
    accounts: 'Ad accounts',
    campaignsCount: 'Campaigns',
    leadsTotal: 'Leads total',
    leadsWeek: 'Leads (7 days)',
    connections: 'Connections',
    connect: 'Connect LinkedIn',
    reconnect: 'Reconnect',
    disconnect: 'Disconnect',
    confirmDisconnect: 'Disconnect “{{label}}”? Data already synced stays.',
    noConnection: 'No LinkedIn connection yet. Connect a LinkedIn account that has roles on the ad accounts and the company page.',
    connectHint: 'You will be sent to LinkedIn to confirm read access to ad accounts, statistics and leads.',
    status: { active: 'Active', expired: 'Expired', revoked: 'Revoked', error: 'Error' },
    connectedAt: 'Connected on',
    tokenValidUntil: 'Access valid until',
    refreshValidUntil: 'Renewable until',
    noRefresh: 'No automatic renewal — reconnect when it expires.',
    lastFullSync: 'Last full sync',
    lastLeadSync: 'Last lead sync',
    syncFull: 'Sync everything',
    syncLeads: 'Sync leads',
    syncQueued: 'Sync started.',
    runs: 'Recent runs',
    run: { full: 'Full', leads: 'Leads', analytics: 'Statistics', lead: 'Webhook lead' },
    runStatus: { running: 'running', ok: 'ok', failed: 'failed' },
    noRuns: 'No runs yet.',
    connectResult: {
      ok: 'LinkedIn is connected. The first sync is running.',
      connected_no_accounts: 'Connected, but the account sees no ad accounts. Check the roles in LinkedIn Campaign Manager.',
      cancelled: 'The connection was cancelled on LinkedIn.',
      invalid: 'The connection could not be completed. Please try again.',
      exchange_failed: 'LinkedIn did not confirm access. Please try again.'
    },
    webhooks: 'Real-time notifications',
    webhookUrl: 'Webhook URL',
    webhookUnreachable: 'LinkedIn cannot reach this address (public HTTPS only). Webhooks work once deployed.',
    webhookSteps: 'Once: register and validate this URL in the LinkedIn Developer Portal under “Webhooks”. Then subscribe here.',
    subscribe: 'Subscribe all accounts',
    subscribed: '{{created}} new, {{existing}} existing, {{failed}} failed.',
    noSubscriptions: 'No subscriptions.',
    account: 'Account',
    role: 'Role',
    currency: 'Currency',
    campaign: 'Campaign',
    group: 'Campaign group',
    objective: 'Objective',
    impressions: 'Impressions',
    clicks: 'Clicks',
    cost: 'Cost',
    period: 'Schedule',
    allAccounts: 'All accounts',
    noCampaigns: 'No campaigns synced yet.'
  },
  leads: {
    title: 'Leads',
    search: 'Search (name, company{{email}})',
    searchEmail: ', e-mail',
    includeTest: 'Show test leads',
    submitted: 'Submitted',
    name: 'Name',
    company: 'Company',
    email: 'E-mail',
    campaign: 'Campaign',
    form: 'Form',
    none: 'No leads found.',
    test: 'Test',
    withdrawn: 'Withdrawn',
    erased: 'Erased (GDPR)',
    masked: 'Contact data is hidden — you lack the permission.',
    answers: 'Answers',
    consents: 'Consents',
    consentGiven: 'given',
    consentDenied: 'not given',
    details: 'Details',
    jobTitle: 'Job title',
    country: 'Country',
    phone: 'Phone',
    firstSeen: 'In Holymoly since',
    exportCsv: 'Export CSV',
    exportHint: 'Exports the filtered leads (at most 20,000). Every export is logged.',
    erase: 'Erase personal data',
    eraseTitle: 'Erase personal data (GDPR)',
    eraseText: 'Name, contact data, answers and the raw LinkedIn data are removed irreversibly. Date, form and campaign stay for statistics. A later LinkedIn sync does not refill the data.',
    eraseSameEmail: 'Erase all leads with this e-mail address',
    eraseConfirm: 'Erase permanently',
    erasedDone: '{{count}} lead(s) anonymised.',
    eraseCrmNote: 'There are {{count}} related lead(s) in the CRM. Delete them there separately:'
  },
  crm: {
    title: 'CRM sync',
    section: 'CRM',
    queue: 'Transfers',
    mapping: 'Field mapping',
    notConfigured: 'No CRM is set up on this server (CRM_INSTANCE_URI is missing).',
    instance: 'CRM instance',
    mode: 'Sync',
    modeLive: 'Live – leads are created',
    modeDryRun: 'Dry run – nothing is sent to the CRM',
    syncOn: 'Sync on',
    on: 'On',
    off: 'Off',
    syncOff: 'Sync off',
    syncOffHint: 'New leads are held until you turn the sync on and release them.',
    enable: 'Turn on',
    disable: 'Turn off',
    confirmEnable: 'Turn the sync on? New leads then go to the CRM automatically (at most {{cap}}). Leads already held stay held until you release them.',
    confirmEnableUnlimited: 'Turn the sync on? New leads then go to the CRM automatically, without a limit. Leads already held stay held until you release them.',
    cap: 'Limit',
    capUsage: '{{sent}} of {{cap}} sent',
    capUnlimited: 'No limit ({{sent}} sent)',
    metadata: 'CRM fields',
    metadataSyncedAt: 'As of {{date}}',
    metadataNever: 'Not loaded yet.',
    refreshMetadata: 'Reload',
    metadataQueued: 'Reloading fields – refresh in a few seconds.',
    held: 'Held',
    releaseAll: 'Release all ({{count}})',
    releaseSelected: 'Release selected ({{count}})',
    confirmReleaseAll: 'Release {{count}} held leads? They are sent to the CRM as far as the limit allows.',
    released: '{{count}} leads released.',
    retry: 'Retry',
    retried: 'Transfer is being retried.',
    release: 'Release',
    openInCrm: 'Open in CRM',
    noRows: 'No transfers.',
    attempts: 'Attempts',
    lead: 'Lead',
    createdAt: 'Queued',
    sentAt: 'Sent',
    error: 'Error',
    requestId: 'Request ID',
    filterAll: 'All',
    statuses: {
      pending: 'Waiting',
      sending: 'Sending',
      sent: 'Sent',
      failed: 'Failed',
      held: 'Held',
      skipped: 'Skipped',
      simulated: 'Dry run'
    },
    holdReasons: {
      disabled: 'Sync off',
      cap: 'Limit reached',
      consent_missing: 'Consent missing',
      match_ambiguous: 'Contact ambiguous',
      mapping_incomplete: 'Neither e-mail nor last name'
    },
    skipReasons: { test_lead: 'Test lead', erased: 'Personal data erased' },
    match: {
      linked: 'Linked to contact',
      linked_most_recent: 'Linked to most recent contact',
      ambiguous: 'Several contacts – not linked, please check',
      none: 'No contact found'
    },
    leadCard: 'CRM',
    notQueued: 'This lead was never queued for the CRM.',
    fieldMap: 'Fields',
    fieldMapHint: 'Where each value comes from and which CRM lead field receives it.',
    source: 'Source',
    attribute: 'CRM field',
    type: 'Type',
    types: { string: 'Text', number: 'Number', boolean: 'Yes/No', picklist: 'Choice', date: 'Date', json: 'JSON field' },
    jsonKey: 'Key',
    jsonHint: 'Type “JSON field”: all rows with the same CRM field are combined into one JSON object ({"key": "value"}), e.g. for utm_content.',
    addRow: 'Add row',
    removeRow: 'Remove row',
    sourceKinds: { field: 'Lead field', answer: 'Form answer', meta: 'Metadata', constant: 'Fixed value', template: 'Template' },
    leadFields: { firstName: 'First name', lastName: 'Last name', email: 'E-mail', phone: 'Phone', company: 'Company', jobTitle: 'Job title', country: 'Country', city: 'City' },
    metaKeys: { linkedinLeadId: 'LinkedIn lead ID', form: 'Form name', campaign: 'Campaign name', submittedAt: 'Submitted at' },
    sourceValue: 'Value',
    valueMap: 'Answer → choice value',
    valueMapHint: 'One line per answer: answer text = number (option value in the CRM).',
    fixed: 'Fixed values',
    subjectTemplate: 'Topic',
    subjectHint: 'Placeholders: {{placeholders}}',
    leadSource: 'Lead source',
    linkedinIdAttribute: 'Field for the LinkedIn lead ID',
    linkedinIdHint: 'Lets every CRM lead be traced to its LinkedIn lead. Empty = not stored.',
    none: '– none –',
    matching: 'Contact matching',
    contactsActiveOnly: 'Only consider active contacts',
    multipleMatches: 'When several contacts share the e-mail address',
    multiple: { flag: 'Do not link, flag it', link_most_recent: 'Link the most recently modified contact', skip_links: 'Do not link' },
    consents: 'Required consents',
    consentsHint: 'LinkedIn consent IDs, comma-separated. Leads without these consents are held.',
    includeTestLeads: 'Also transfer test leads',
    campaigns: 'Campaigns',
    campaignsHint: 'LinkedIn campaign → CRM campaign (set on the lead).',
    noCrmCampaign: '– none –',
    save: 'Save',
    saved: 'Saved.',
    preview: 'Preview',
    previewHint: 'Builds the CRM lead for a real lead with the mapping above, without sending anything. Contact matching is not part of the preview.',
    previewLead: 'Lead ID',
    previewRun: 'Build preview',
    previewSkipped: 'No value: {{attributes}}',
    unsaved: 'Unsaved changes'
  },
  dashboards: {
    title: 'Dashboards',
    section: 'Dashboards',
    new: 'New dashboard',
    name: 'Name',
    template: 'Template',
    templates: { empty: 'Empty', linkedin_performance: 'LinkedIn performance', lead_funnel: 'Leads and CRM' },
    visibility: 'Visibility',
    visibilityAll: 'Everyone with dashboard access',
    visibilityRestricted: 'Only shared people and roles',
    create: 'Create',
    none: 'No dashboards yet.',
    owner: 'Owner',
    updated: 'Changed',
    edit: 'Edit',
    statuses: { draft: 'Draft', published: 'Published', proposed: 'Proposal', rejected: 'Rejected' },
    proposals: 'Proposals: {{count}}',
    notPublished: 'Not published yet',
    viewingVersion: 'You are looking at version {{version}} ({{status}}).',
    showPublished: 'Show the published version',
    noData: 'No data in the selected period.',
    loadError: 'Could not be loaded.',
    vsPrevious: 'vs. previous period',
    period: 'Period',
    from: 'From',
    to: 'To',
    custom: 'Custom',
    all: 'All',
    selected: '{{count}} selected',
    presets: {
      today: 'Today',
      yesterday: 'Yesterday',
      last_7_days: 'Last 7 days',
      last_14_days: 'Last 14 days',
      last_30_days: 'Last 30 days',
      last_90_days: 'Last 90 days',
      last_365_days: 'Last 365 days',
      this_week: 'This week',
      last_week: 'Last week',
      this_month: 'This month',
      last_month: 'Last month',
      this_quarter: 'This quarter',
      last_quarter: 'Last quarter',
      this_year: 'This year',
      last_year: 'Last year'
    },
    rowCount: '{{count}} rows',
    previousPage: 'Previous',
    nextPage: 'Next'
  },
  builder: {
    drag: 'Drag to move',
    back: 'Back to the dashboard',
    basedOn: 'Editing based on version {{version}}',
    visual: 'Visual',
    code: 'Code',
    versions: 'Versions',
    sharing: 'Sharing',
    notePlaceholder: 'Note on the change (optional)',
    saveDraft: 'Save draft',
    publish: 'Publish',
    savedDraft: 'Saved as version {{version}} (draft).',
    published: 'Published.',
    issues: 'Problems: {{count}} – cannot be saved like this.',
    add: 'Add:',
    widget: 'Element',
    dashboard: 'Dashboard',
    dashboardSettings: 'Dashboard settings',
    widgetTitle: 'Heading',
    type: 'Display',
    types: { kpi: 'Key figure', timeseries: 'Time series', bar: 'Bars', donut: 'Donut', table: 'Table', markdown: 'Text' },
    content: 'Text',
    markdownHint: 'Markdown: # heading, **bold**, *italic*, - list, [link](https://…)',
    removeWidget: 'Remove element',
    data: 'Data',
    dataset: 'Data source',
    measures: 'Measures',
    dimensions: 'Split by',
    none: '– none –',
    grain: 'Time unit',
    grains: { day: 'Day', week: 'Week', month: 'Month', quarter: 'Quarter', year: 'Year' },
    appliesFilters: 'Apply filters',
    limit: 'At most rows',
    display: 'Display',
    compare: 'Compare with previous period',
    chart: 'Chart type',
    charts: { line: 'Line', area: 'Area', bar: 'Columns' },
    stacked: 'Stacked',
    horizontal: 'Horizontal',
    pageSize: 'Rows per page',
    dashboardTitle: 'Title',
    description: 'Description',
    filters: 'Filters',
    removeFilter: 'Remove filter',
    filterLabel: 'Label',
    multi: 'Multiple choice',
    addDateFilter: 'Period',
    addSelectFilter: 'Choice filter',
    viaToken: 'Created via an API token (e.g. by an AI agent)',
    compareWith: 'Version {{a}} → {{b}}',
    loadIntoEditor: 'Load into the editor',
    reject: 'Reject',
    rejectedDone: 'Proposal rejected.',
    accept: 'Accept and publish',
    restore: 'Restore',
    noChanges: 'No differences.',
    sharesHint: 'Also share with:',
    access: 'Access',
    viewer: 'View',
    editor: 'Edit',
    removeShare: 'Remove share',
    addShare: 'Add a person or role …'
  },
  datasets: {
    title: 'Data sources',
    new: 'New data source',
    editTitle: 'Edit data source {{id}}',
    notConfigured: 'BigQuery is not set up on this server (BIGQUERY_PROJECT is missing). The built-in Holymoly data sources still work.',
    project: 'GCP project',
    location: 'Location',
    maxBytes: 'At most per query',
    credentials: 'Sign-in',
    credentialKinds: { workload_identity: 'Workload Identity Federation (no key)', service_account_key: 'Service account key', default: 'Default credentials', none: '–' },
    allowed: 'Allowlisted BigQuery datasets',
    dataset: 'Data source',
    source: 'Source',
    usedBy: 'Used in',
    builtin: 'built-in',
    disabled: 'disabled',
    invalid: 'invalid',
    enabled: 'Active',
    table: 'BigQuery table',
    loadColumns: 'Load columns',
    editorHint: 'Measures are sums, counts or ratios of columns – no SQL. Date filters need a DATE column.',
    confirmDelete: 'Delete data source {{id}}?'
  },
  languages: {
    de: 'Deutsch',
    en: 'English'
  },
  overview: {
    title: 'Overview',
    systemStatus: 'System status',
    statusOk: 'All services reachable',
    statusDegraded: 'Degraded',
    database: 'Database',
    redis: 'Queue (Redis)',
    version: 'Version',
    reachable: 'reachable',
    unreachable: 'unreachable',
    latency: '{{ms}} ms'
  },
  notFound: {
    title: 'Page not found',
    description: 'This page does not exist (anymore).',
    back: 'Back to overview'
  },
  mail: {
    greeting: 'Hello,',
    signature: 'Best regards\nHolymoly · Bechtle PLM Marketing',
    invite: {
      subject: 'Invitation to Holymoly',
      body: '{{inviter}} invited you to Holymoly, the marketing platform of Bechtle PLM.\n\nSet your password with this link (valid until {{expires}}):\n{{url}}\n\nIf this invitation means nothing to you, simply ignore this email.'
    },
    crmFailures: {
      subject: 'Holymoly: {{count}} lead(s) could not be transferred to the CRM',
      body: '{{count}} lead(s) were rejected by the CRM or could not be transferred after several attempts:\n\n{{lines}}\n\nDetails and "Retry":\n{{url}}\n\n(No contact data here, for data protection.)'
    },
    resetPassword: {
      subject: 'Holymoly: reset your password',
      body: 'A new password was requested for your Holymoly account.\n\nSet it with this link (valid for one hour):\n{{url}}\n\nNot you? Ignore this email — your password stays unchanged.'
    }
  },
  errors: {
    UNAUTHORIZED: 'Please sign in.',
    FORBIDDEN: 'You do not have permission for this.',
    NOT_FOUND: 'Not found.',
    CONFLICT: 'This was changed in the meantime. Please reload.',
    RATE_LIMITED: 'Too many requests. Please wait a moment.',
    TWO_FACTOR_REQUIRED: 'Set up two-factor authentication first.',
    INTERNAL_SERVER_ERROR: 'Something went wrong on the server. Please try again later.',
    BAD_REQUEST: 'The input is invalid.',
    NETWORK: 'No connection to the server.'
  }
};
